/**
 * CKEditor 5 plugin: merge tokens (template variables) as inline, non-editable widgets.
 * Persists as <span class="merge-token" data-key="..." data-token="...">Label</span>
 * so templateUtils/renderTemplate and sanitizeHtml keep working.
 */

import { Plugin, Command } from 'ckeditor5';

export default class MergeTokenPlugin extends Plugin {
  static get pluginName() {
    return 'MergeToken';
  }

  init() {
    const editor = this.editor;
    const schema = editor.model.schema;
    const conversion = editor.conversion;

    // Model: inline object element with key and token (label) attributes
    schema.register('mergeToken', {
      allowWhere: '$text',
      isObject: true,
      isInline: true,
      allowAttributes: ['key', 'token'],
    });

    // Upcast: <span class="merge-token" data-key="..." data-token="...">...</span> -> mergeToken
    // Custom converter so we don't convert the span's children (mergeToken is an object).
    conversion.for('upcast').add((dispatcher) => {
      dispatcher.on('element:span', (evt, data, conversionApi) => {
        const viewItem = data.viewItem;
        if (!viewItem.hasClass || !viewItem.hasClass('merge-token')) return;
        const key = viewItem.getAttribute('data-key');
        const token = viewItem.getAttribute('data-token') || '';
        if (!key) return;
        if (!conversionApi.consumable.test(viewItem, { name: true })) return;
        const modelElement = conversionApi.writer.createElement('mergeToken', {
          key: String(key),
          token: String(token),
        });
        if (!conversionApi.safeInsert(modelElement, data.modelCursor)) return;
        conversionApi.consumable.consume(viewItem, { name: true });
        conversionApi.updateConversionResult(modelElement, data);
      }, { priority: 'high' });
    });

    // Downcast: mergeToken -> <span class="merge-token" data-key="..." data-token="...">token</span>
    conversion.for('downcast').elementToElement({
      model: 'mergeToken',
      view: (modelElement, { writer }) => {
        const key = modelElement.getAttribute('key') || '';
        const token = modelElement.getAttribute('token') || '';
        return writer.createContainerElement(
          'span',
          {
            class: 'merge-token',
            'data-key': key,
            'data-token': token,
          },
          [writer.createText(token)]
        );
      },
    });

    // Command: insertMergeToken({ key, token, label?, unique? })
    editor.commands.add('insertMergeToken', new InsertMergeTokenCommand(editor));
  }

  /**
   * Returns all merge token keys present in the editor's current data (for uniqueness checks).
   * @param {object} editor - CKEditor instance
   * @returns {string[]}
   */
  static getTokenKeysInEditor(editor) {
    if (!editor || !editor.model) return [];
    const root = editor.model.document.getRoot();
    const keys = [];
    for (const range of root.getWalker()) {
      if (range.item.is('element', 'mergeToken')) {
        const key = range.item.getAttribute('key');
        if (key) keys.push(key);
      }
    }
    return keys;
  }
}

class InsertMergeTokenCommand extends Command {
  execute(options = {}) {
    const editor = this.editor;
    const { key, token, label, unique } = options;
    if (!key || token === undefined) return;

    const model = editor.model;
    const existingKeys = MergeTokenPlugin.getTokenKeysInEditor(editor);
    if (unique && existingKeys.includes(key)) {
      return; // Caller should show onTokenBlocked message
    }

    model.change((writer) => {
      const mergeToken = writer.createElement('mergeToken', {
        key: String(key),
        token: String(token || label || key),
      });
      model.insertContent(mergeToken, model.document.selection);
      writer.setSelection(mergeToken, 'after');
    });
  }

  refresh() {
    const model = this.editor.model;
    const selection = model.document.selection;
    const position = selection.getFirstPosition();
    if (!position) {
      this.isEnabled = false;
      return;
    }
    const allowedParent = model.schema.findAllowedParent(position, 'mergeToken');
    this.isEnabled = allowedParent !== null;
  }
}
